import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-gray-100 dark:bg-slate-900 py-12 px-4">
      <div className="container mx-auto max-w-4xl">
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-blue-600 hover:text-blue-700 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </button>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Privacy Policy</CardTitle>
            <p className="text-slate-600 dark:text-slate-400 text-sm mt-2">Last Updated: January 26, 2025</p>
          </CardHeader>
          <CardContent className="prose dark:prose-invert max-w-none">
            <p className="text-slate-700 dark:text-slate-300">
              At SmartPDF Tools ("we," "us," or "our"), we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our PDF processing services.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">1. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold mt-6 mb-3">1.1 Information You Provide</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li><strong>Account Information:</strong> Email address, password (encrypted), username</li>
              <li><strong>Payment Information:</strong> Processed securely through Stripe (we do not store credit card details)</li>
              <li><strong>File Data:</strong> PDF files you upload for processing (temporary storage only)</li>
              <li><strong>Communications:</strong> Messages you send us via contact forms or support emails</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">1.2 Automatically Collected Information</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li><strong>Usage Data:</strong> Pages visited, features used, time spent, actions performed</li>
              <li><strong>Device Information:</strong> Browser type, operating system, IP address</li>
              <li><strong>Cookies:</strong> Session cookies, authentication tokens, preference settings</li>
              <li><strong>Analytics:</strong> Anonymized usage statistics for service improvement</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">2. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li>To provide, maintain, and improve our PDF processing services</li>
              <li>To process your files (merge, split, compress, convert PDFs)</li>
              <li>To manage your account and subscriptions</li>
              <li>To process payments through Stripe</li>
              <li>To send service-related emails (password resets, payment confirmations)</li>
              <li>To monitor usage limits (3 files/day for free users, unlimited for Pro)</li>
              <li>To detect and prevent abuse, fraud, or unauthorized access</li>
              <li>To comply with legal obligations</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">3. File Processing and Storage</h2>
            <p className="text-slate-700 dark:text-slate-300">
              <strong>Important: Your files are processed temporarily and automatically deleted.</strong>
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300 mt-3">
              <li><strong>Processing Time:</strong> Files are processed immediately upon upload</li>
              <li><strong>Storage Duration:</strong> Processed files are stored for <strong>1 hour only</strong></li>
              <li><strong>Automatic Deletion:</strong> All uploaded and processed files are permanently deleted after 1 hour</li>
              <li><strong>No Long-term Storage:</strong> We do not permanently store your files on our servers</li>
              <li><strong>Secure Processing:</strong> Files are processed in isolated, secure environments</li>
              <li><strong>No Content Analysis:</strong> We do not read, analyze, or use the content of your files for any purpose other than processing</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">4. Data Sharing and Disclosure</h2>
            <p className="text-slate-700 dark:text-slate-300">We do not sell your personal information. We may share information with:</p>
            
            <h3 className="text-xl font-semibold mt-6 mb-3">4.1 Service Providers</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li><strong>Stripe:</strong> Payment processing (subject to Stripe's Privacy Policy)</li>
              <li><strong>SendGrid:</strong> Email delivery for password resets and notifications</li>
              <li><strong>Cloud Infrastructure:</strong> Secure hosting providers (servers, databases)</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">4.2 Legal Requirements</h3>
            <p className="text-slate-700 dark:text-slate-300">
              We may disclose information if required by law, court order, or government request, or to protect our rights, property, or safety.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">5. Your Rights (GDPR & CCPA)</h2>
            <p className="text-slate-700 dark:text-slate-300">You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
              <li><strong>Erasure:</strong> Request deletion of your account and data ("right to be forgotten")</li>
              <li><strong>Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
              <li><strong>Object:</strong> Object to processing of your personal data</li>
              <li><strong>Restrict Processing:</strong> Request limitation of processing in certain circumstances</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent at any time (does not affect prior processing)</li>
              <li><strong>Opt-out of Sale:</strong> We do not sell personal information (CCPA compliance)</li>
            </ul>

            <p className="text-slate-700 dark:text-slate-300 mt-4">
              To exercise these rights, contact us at: <a href="mailto:support@smrtpdftools.com" className="text-blue-600 hover:text-blue-700">support@smrtpdftools.com</a>
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">6. Cookies and Tracking</h2>
            <p className="text-slate-700 dark:text-slate-300">
              We use cookies and similar technologies. See our <a href="/cookie-policy" className="text-blue-600 hover:text-blue-700">Cookie Policy</a> for details.
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300 mt-3">
              <li><strong>Essential Cookies:</strong> Required for authentication and service functionality</li>
              <li><strong>Preference Cookies:</strong> Remember your settings (dark/light mode)</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how you use our service</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">7. Data Security</h2>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li><strong>Encryption:</strong> All data transmitted using SSL/TLS encryption (HTTPS)</li>
              <li><strong>Password Security:</strong> Passwords hashed using bcrypt (industry standard)</li>
              <li><strong>Access Controls:</strong> Restricted access to personal data</li>
              <li><strong>Regular Audits:</strong> Security reviews and updates</li>
              <li><strong>Automatic File Deletion:</strong> Files automatically deleted after 1 hour</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">8. International Data Transfers</h2>
            <p className="text-slate-700 dark:text-slate-300">
              Your data may be processed in countries outside your residence. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">9. Children's Privacy</h2>
            <p className="text-slate-700 dark:text-slate-300">
              Our service is not intended for children under 13 (or 16 in EU). We do not knowingly collect data from children. If you believe a child has provided us with personal information, contact us immediately.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">10. Data Retention</h2>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li><strong>Account Data:</strong> Retained while your account is active</li>
              <li><strong>Files:</strong> Deleted after 1 hour</li>
              <li><strong>Usage Logs:</strong> Retained for 90 days</li>
              <li><strong>Payment Records:</strong> Retained as required by law (typically 7 years)</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">11. Changes to This Privacy Policy</h2>
            <p className="text-slate-700 dark:text-slate-300">
              We may update this Privacy Policy from time to time. We will notify you of material changes by email or prominent notice on our website. Your continued use after changes constitutes acceptance.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">12. Contact Us</h2>
            <p className="text-slate-700 dark:text-slate-300">
              For questions about this Privacy Policy or to exercise your rights:
            </p>
            <ul className="list-none pl-0 space-y-2 text-slate-700 dark:text-slate-300 mt-3">
              <li><strong>Email:</strong> <a href="mailto:support@smrtpdftools.com" className="text-blue-600 hover:text-blue-700">support@smrtpdftools.com</a></li>
              <li><strong>Support:</strong> <a href="mailto:support@smrtpdftools.com" className="text-blue-600 hover:text-blue-700">support@smrtpdftools.com</a></li>
              <li><strong>Website:</strong> <a href="https://smrtpdftools.com" className="text-blue-600 hover:text-blue-700">https://smrtpdftools.com</a></li>
            </ul>

            <div className="mt-12 p-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-slate-700 dark:text-slate-300">
                <strong>Your Privacy Matters:</strong> We are committed to transparency and protecting your data. If you have concerns or questions, we're here to help.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;